import { Document } from '@contentful/rich-text-types';
import { Asset, Entry } from 'contentful';
import { ReactElement } from 'react';
import { FlairsValue } from '../theme';
import { TPlanType } from '../ui-components/PlanCard.types';
import { ICalloutProps } from '../ui-components/Callout.types';
import { IIcon } from '../icons/icon';
import { IAccordionRow } from '../contentful/actions/AccordionRow';
import { PRODUCT_FAMILY } from '../product';
import { IMoleculeAlert } from '../alert';

export enum NBN_PRODUCT_STATUS {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive'
}

export enum NBN_PRODUCT_PAYMENT_FREQUENCY {
  ONE_TIME = 'One-time',
  RECURRING = 'Recurring'
}

export enum NBN_PRODUCT_CATEGORY_NAME {
  NBN = 'nbn',
  NDC = 'NDC'
}

export interface IModemAndNdcRadioSelectorsOption {
  value: string;
  productCode: string;
  price: number;
  id: string;
  label: string;
  sideLabel?: string;
  copy?: string | ReactElement<any>;
  'data-testid'?: string;
  'aria-label'?: string;
}

/**
 * represents the 'Element > Feature Row' content model
 */
export type FeatureRow = {
  description: Document;
  icon: Asset;
  identifier: string;
};

export type FeatureRowEntry = Entry<FeatureRow>;

/**
 * represents the 'Catalog > Addon' content model
 */
export interface ICatalogAddon {
  chargeType: string;
  description: string;
  priceFrequency: string;
  priceUnit: string;
  priceValue: number;
  productCode: string;
  productIdentifier: string;
  productName: string;
}

/**
 * represents the 'Catalog > Products' content model
 */
export interface ICatalogProduct {
  addons: Entry<ICatalogAddon>[];
  chargeType: string;
  dataUnit: string;
  dataValue: number;
  description: string;
  included: string[];
  priceFrequency: string;
  priceUnit: string;
  priceValue: number;
  productCode: string;
  productIdentifier: string;
  productName: string;
  productFamily: PRODUCT_FAMILY;
  productType: string;
  availability: string;
}

/**
 * represents the 'Catalog > nbn products' content model
 */
export interface ICatalogNbnProduct {
  productName: string;
  categoryName: NBN_PRODUCT_CATEGORY_NAME;
  productCode: string;
  planProductCode: string;
  price: number;
  upstreamSpeedMbps?: number;
  downstreamSpeedMbps?: number;
  eveningUpstreamSpeedMbps?: number;
  eveningDownstreamSpeedMbps?: number;
  frequency: NBN_PRODUCT_PAYMENT_FREQUENCY;
  status?: NBN_PRODUCT_STATUS;
  permitted?: boolean;
  promoCode?: string;
  campaignCode?: string;

  // Legacy nbn plans
  expiryDate?: string;
  futurePrice?: number;
}

/**
 * represents the 'Element > Plan Card' content model
 */
export interface IElementPlanCard<T extends ICatalogNbnProduct | ICatalogProduct> {
  accentStripColour?: string;
  calloutSubCopy?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  features?: FeatureRowEntry[];
  featuresLabel: string;
  identifier: string;
  infoText?: any; // IInfoBox;
  nbnPlanUnavailableLabel?: string;
  planCallout?: any; // ICalloutProps;
  planDifferentiator?: string;
  planDifferentiatorColour?: string;
  planSpeedDifferentiator?: string;
  planSpeedDifferentiatorColour?: string;
  planType?: string;
  product: Entry<T>;
  nbnSpeedConfirmedSupportingInfo?: Document;
  supportingCopy?: Document;
  uploadSpeedLabel?: string;
  downloadSpeedLabel?: string;
}

/**
 * represents the 'Organism > Plan Card' content model
 */
export interface IOrganismPlanCard<T extends ICatalogNbnProduct | ICatalogProduct> {
  planType?: TPlanType;
  cardStripColour?: FlairsValue;
  differentiatorIcon?: IIcon;
  differentiatorLabel?: string;
  title: string;
  product: Entry<T>;
  dataBeforePromo?: number;
  priceBeforePromo?: number;
  alert?: IMoleculeAlert;
  promoCallout?: ICalloutProps;
  typicalSpeedsCopy?: Document;
  inclusions?: IAccordionRow[];
  addOns?: IAccordionRow[];
  suitability?: IAccordionRow[];
  disclaimer?: Document;
  activityLevel?: Document;
  buttonLabel: string;
  // will be removed once Fibre Connect Premium Plan is replaced by High Speed Plan which has its own product code
  isFibreConnect?: boolean;
}

export type NbnOrganismPlanCardEntry = Entry<
  Omit<IOrganismPlanCard<ICatalogNbnProduct>, 'dataBeforePromo' | 'priceBeforePromo' | 'alert'> & {
    dataBeforePromo?: string;
    priceBeforePromo?: string;
    alert: Entry<any>;
  }
>;
